import React, { useEffect } from 'react';
import { connectLean } from 'lean-redux';
import { withApollo } from 'react-apollo';
import mergeWith from 'lodash/mergeWith';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import log from 'chameleon/ui-stack/utilities/log';
import { getOutlineSelectedClasses } from 'legacy/packages/unit-outline/src/queries';
import { userVideoResourceStatusListQuery } from 'legacy/src/queries';
import getCourseOutlineResourcesByType from 'legacy/src/utility/helpers/getCourseOutlineResourcesByType';
import ResourceTypes from 'legacy/packages/unit-outline/src/constants';

const connectLeanMethods = {
  scope: 'units',
  setResourcesWatchedStatus(videoResourceStatuses, subjectId) {
    let videoResourceStatusesMap = {};
    let startedVideoResourceStatusesMap = {};
    if (videoResourceStatuses.length) {
      videoResourceStatuses.forEach(({ data }) => {
        data.forEach(({ resourceId, status }) => {
          videoResourceStatusesMap[
            `resource-${resourceId}-subject-${subjectId}`
          ] = status === 'COMPLETE';
          startedVideoResourceStatusesMap[
            `resource-${resourceId}-subject-${subjectId}`
          ] = ['COMPLETE', 'STARTED'].includes(status);
        });
      });
    }

    const unitsState = this.state;
    forEach(unitsState, ({ resources }, unitIndex) => {
      forEach(resources || {}, (resource, resourceIndex) => {
        unitsState[unitIndex].resources[resourceIndex].withWatchedVideo =
          videoResourceStatusesMap[resourceIndex] || false;
        unitsState[unitIndex].resources[resourceIndex].isStarted =
          startedVideoResourceStatusesMap[resourceIndex] || false;
      });
    });

    this.setState(unitsState);
  },
  /**
   * Set shared items
   * @note for teacher
   * @param {array} sharedUnits List of shared units
   * @param {array} sharedResources List of shared resources
   * @param {array} rawUnits List of all units
   * @param {array} userVideoResourceStatusList List of shared resource with statuse
   * */
  setSharedItems(sharedUnits, sharedResources, rawUnits, subjectId) {
    /**
     * Create shareable resources for a unit
     * @param {string} unitNumber The unit id
     * @param {array} units The list of units
     * @param {array} sharedResources The list of resources
     * @return {object} The resources to be saved in store
     * */
    const createShareableResources = (
      unitNumber,
      units,
      sharedResources = [],
    ) => {
      const foundUnit = units.find((unit) => unit.number === unitNumber) || {};
      if (!isEmpty(foundUnit)) {
        const resources = {};
        (foundUnit.subunits || []).forEach((subUnit) => {
          (subUnit.resources || []).forEach((resource) => {
            if (
              resource.isShareable ||
              resource.__typename === 'AssessmentResource'
            ) {
              const sharedResource = sharedResources.find(
                (sharedResource) => sharedResource.id === resource.resourceId,
              );

              let resourceStore = {};
              if (!isEmpty(sharedResource)) {
                const { classIds } = sharedResource;
                resourceStore = {
                  savedClasses: classIds,
                  selectedItems: classIds,
                };
              }

              resources[
                `resource-${resource.resourceId}-subject-${subjectId}`
              ] = {
                ...resourceStore,
                disabled: false,
              };
            }
          });
        });
        return resources;
      }
      return {};
    };

    let units = {
      isSharedItemSet: true,
    };
    (rawUnits || []).forEach(({ number }) => {
      const { classIds = [] } =
        sharedUnits.find((item) => item.number === number) || {};
      units[`unit-${number}`] = {
        savedClasses: classIds,
        selectedItems: classIds,
        resources: createShareableResources(number, rawUnits, sharedResources),
      };
    });

    this.setState((prevState) =>
      mergeWith(prevState, units, (prevValue, newValue) => {
        const hasPrevValue =
          prevValue &&
          Object.prototype.hasOwnProperty.call(prevValue, 'savedClasses');
        const hasNoNewValue =
          newValue &&
          !Object.prototype.hasOwnProperty.call(newValue, 'savedClasses');
        if (hasPrevValue && hasNoNewValue) {
          return {
            ...prevValue,
            ...newValue,
          };
        }
      }),
    );
  },
};

const ShareHelper = (WrappedComponent) => {
  const Share = (props) => {
    const {
      courseOutline: { units = [] } = {},
      userClasses,
      userData,
      params: { subject },
    } = props;

    const loadVideoResourceStatuses = async (id, units, userData) => {
      const unitsVideoResources = getCourseOutlineResourcesByType(
        units,
        ResourceTypes.EMBEDDED_VIDEO,
      );
      const videoIds = unitsVideoResources.map(({ videoId }) =>
        Number.parseInt(videoId, 10),
      );

      const { client, setResourcesWatchedStatus } = props;
      let response;
      try {
        response = await client.query({
          query: userVideoResourceStatusListQuery,
          fetchPolicy: 'network-only',
          variables: {
            subjectId: Number.parseInt(id, 10),
            userIds: [Number.parseInt(userData.me.initId, 10)],
            videoIds,
          },
        });
      } catch (error) {
        log('error', error);
      }

      if (response) {
        const {
          userVideoResourceStatusList: userVideoResourceStatusListResponse,
        } = response.data;

        if (userVideoResourceStatusListResponse) {
          let videoResourceStatuses = [];
          try {
            const { dailyVideoStatus } =
              JSON.parse(userVideoResourceStatusListResponse) || {};
            videoResourceStatuses =
              dailyVideoStatus.userVideoResourceStatusList || [];
          } catch (error) {
            log('error', error);
          }

          setResourcesWatchedStatus(videoResourceStatuses, id);
        }
      }
    };

    /**
     * Load the shared classes
     * @param {string} subjectId The selected subject id
     * @param {array} rawUnits The list of raw units
     * @param {array} userClasses The list of classes
     * */
    const loadSharedClasses = async (subjectId, rawUnits, userClasses) => {
      const unitIds = rawUnits.map((item) => item.unitId);
      const resourceIds = getResourcesFromUnits(rawUnits);
      const classIds = userClasses.map((item) => item.id);

      const { client, setSharedItems } = props;

      let response;
      try {
        response = await client.query({
          query: getOutlineSelectedClasses,
          fetchPolicy: 'network-only',
          variables: {
            subjectId: Number.parseInt(subjectId, 10),
            unitIds,
            resourceIds,
            classIds: classIds.map((classId) => Number.parseInt(classId, 10)),
          },
        });
      } catch (error) {
        log('error', error);
      }

      if (response) {
        const {
          data: { sharedCourseOutline },
        } = response;
        const { units = [], resources = [] } = sharedCourseOutline || {};

        let sharedUnits = units || [];
        const sharedResources = resources || [];

        setSharedItems(sharedUnits, sharedResources, rawUnits, subjectId);
      }
    };

    /**
     * Get resources ids from units
     * @param {array} units List of units
     * @return {array} List of resources
     * */
    const getResourcesFromUnits = (units = []) => {
      let resources = [];
      units.forEach((unit) => {
        (unit.subunits || []).forEach((subunit) => {
          (subunit.resources || []).forEach((resource) => {
            resources.push(resource.resourceId);
          });
        });
      });

      return resources;
    };

    useEffect(() => {
      //When userClasses is empty allow the useEffect to run
      if (userClasses === undefined) {
        return;
      }

      (async () => {
        const {
          me: { sections },
          isStudent,
          educationPeriod: { currentEducationPeriod },
          chosenEducationPeriodCode,
        } = userData;
        const subjectId = subject.toString();
        const subjectClasses = sections.filter(
          (section) => section.masterSubjectId === subjectId,
        );
        let userClassesList = (userClasses || []).filter(
          (item) =>
            `${item.educationPeriod}` ===
            (chosenEducationPeriodCode || currentEducationPeriod.id),
        );
        userClassesList =
          userClassesList.length === 0 && isStudent
            ? subjectClasses
            : userClassesList;
        await loadSharedClasses(subjectId, units, userClassesList);
        if (userData.isStudent) {
          await loadVideoResourceStatuses(subjectId, units, userData);
        }
      })();
    }, [userClasses]);

    return <WrappedComponent {...props} />;
  };

  return connectLean(connectLeanMethods)(withApollo(Share));
};

export default ShareHelper;
